import { useEffect, useMemo, useState } from 'react'
import {
  fetchApprovalPolicies,
  createApprovalPolicy,
  deleteApprovalPolicy,
  type ApprovalPolicyRow,
} from '../../api/ws'
import { useAppStore } from '../../store/app'

// Approvers screen — typed CRUD over approval_policies. Answers
// "who do we ask?" when Authorize() returns ask. See
// docs/approver-resolution.md §"Editor / UX surface".
//
// Each row names (resource_kind, resource_id, action_pattern) — the
// gate — plus the approver (kind + id). Wildcards use '*'. The
// catch-all founder seed at org creation is shown prominently with
// a "covers everything not specified elsewhere" hint.
//
// Reachable via gear → About → Debug → "Approvers".
export function ApproversSheet() {
  const [rows, setRows] = useState<ApprovalPolicyRow[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const orgID = useAppStore((s) => s.organizationID)
  const positionsInOrg = useAppStore((s) =>
    s.organizationPositions.filter((p) => !p.eliminated_at),
  )
  const agentsInOrg = useAppStore((s) =>
    s.agentInstances.filter((a) => !a.retired_at),
  )
  // Position → live agent occupants. Materialized client-side; mirrors
  // what the server's ResolveApprovers does at evaluation time
  // (ai_agent_instances WHERE organization_position_id = ?). Operators
  // pick a position knowing exactly which agents will be in the
  // delegated approver set today.
  const occupantsByPosition = useMemo(() => {
    const m = new Map<string, { id: string; name: string }[]>()
    for (const a of agentsInOrg) {
      const list = m.get(a.organization_position_id) ?? []
      list.push({ id: a.id, name: a.name })
      m.set(a.organization_position_id, list)
    }
    return m
  }, [agentsInOrg])

  type ApproverKind = 'human' | 'agent' | 'position' | 'org_member'
  const [form, setForm] = useState({
    resource_kind: '*',
    resource_id: '*',
    action_pattern: '*',
    approver_kind: 'human' as ApproverKind,
    approver_id: '',
  })
  // Inline edit: when non-null, the form is in "edit mode" — submit
  // does a delete-old + create-new (since approval_policies has UNIQUE
  // on the full tuple, every field is part of identity, so there's no
  // in-place update). Cancel clears editing and resets the form.
  const [editing, setEditing] = useState<ApprovalPolicyRow | null>(null)

  async function refresh() {
    setBusy(true)
    try {
      const resp = await fetchApprovalPolicies()
      if (resp.error) setError(resp.error)
      else setError(null)
      setRows(resp.rows ?? [])
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  // Pre-fill approver_id when switching to org_member — it must
  // equal the current organization_id by invariant. Other kinds
  // leave it blank for the operator to fill.
  useEffect(() => {
    if (form.approver_kind === 'org_member') {
      setForm((f) => ({ ...f, approver_id: orgID ?? '' }))
    }
  }, [form.approver_kind, orgID])

  async function submitForm(e: React.FormEvent) {
    e.preventDefault()
    if (!form.approver_id.trim()) {
      setError('approver_id is required')
      return
    }
    if (form.approver_kind === 'agent') {
      const ok = confirm(
        'Adding an agent as an approver delegates approval authority to its system prompt.\n\n' +
          'The agent will receive every matching request and decide based on whatever its instructions tell it to do. ' +
          'Make sure the agent’s instructions are sound for the actions it’s approving.\n\n' +
          'Cost note: each approved request consumes the agent’s tokens.\n\nContinue?',
      )
      if (!ok) return
    }
    setBusy(true)
    try {
      // Edit = delete-old + create-new (UNIQUE on full tuple means
      // there's no in-place update). Order matters: delete first so
      // the create can't trip the UNIQUE constraint when only
      // approver_id changed.
      if (editing) {
        const del = await deleteApprovalPolicy(editing.id)
        if (del.error) {
          setError(`delete previous row: ${del.error}`)
          return
        }
      }
      const resp = await createApprovalPolicy({
        resourceKind: form.resource_kind.trim() || '*',
        resourceID: form.resource_id.trim() || '*',
        actionPattern: form.action_pattern.trim() || '*',
        approverKind: form.approver_kind,
        approverID: form.approver_id.trim(),
      })
      if (resp.error) {
        setError(
          editing
            ? `create replacement row: ${resp.error} (old row already deleted)`
            : resp.error,
        )
        return
      }
      setError(null)
      setEditing(null)
      setForm({
        resource_kind: '*',
        resource_id: '*',
        action_pattern: '*',
        approver_kind: 'human',
        approver_id: '',
      })
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  function startEdit(row: ApprovalPolicyRow) {
    setEditing(row)
    setForm({
      resource_kind: row.resource_kind,
      resource_id: row.resource_id,
      action_pattern: row.action_pattern,
      approver_kind: row.approver_kind,
      approver_id: row.approver_id,
    })
    // Scroll to the form is implicit — it's at the top of the sheet.
  }

  function cancelEdit() {
    setEditing(null)
    setForm({
      resource_kind: '*',
      resource_id: '*',
      action_pattern: '*',
      approver_kind: 'human',
      approver_id: '',
    })
    setError(null)
  }

  async function onDelete(row: ApprovalPolicyRow) {
    const label = describeRow(row)
    if (!confirm(`Delete approval policy?\n\n${label}`)) return
    setBusy(true)
    try {
      const resp = await deleteApprovalPolicy(row.id)
      if (resp.error) {
        setError(resp.error)
        return
      }
      setError(null)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  const catchAll = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.resource_kind === '*' && r.resource_id === '*' && r.action_pattern === '*',
      ),
    [rows],
  )
  const specific = useMemo(
    () =>
      rows.filter(
        (r) =>
          !(r.resource_kind === '*' && r.resource_id === '*' && r.action_pattern === '*'),
      ),
    [rows],
  )

  // Sole-agent-approver warning: per docs/approver-resolution.md
  // §"Liveness and the 'no timeout' rule," a gate whose materialized
  // approver set is agent-only depends entirely on the agent's daemon
  // being online. We detect this client-side at the EXACT-gate level
  // (same resource_kind + resource_id + action_pattern tuple) — we
  // don't try to factor in wildcard inheritance, which is the
  // resolver's job at evaluation time. The check still catches the
  // obvious "I added an agent and forgot the human fallback" case.
  const lonelyAgentGates = useMemo(() => {
    const buckets = new Map<string, ApprovalPolicyRow[]>()
    for (const r of rows) {
      const key = `${r.resource_kind}|${r.resource_id}|${r.action_pattern}`
      const existing = buckets.get(key)
      if (existing) existing.push(r)
      else buckets.set(key, [r])
    }
    const out: ApprovalPolicyRow[][] = []
    for (const group of buckets.values()) {
      if (group.length === 0) continue
      const everyApproverIsAgent = group.every((r) => r.approver_kind === 'agent')
      if (everyApproverIsAgent) out.push(group)
    }
    return out
  }, [rows])

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ opacity: 0.65, marginTop: 0 }}>
        Approvers decide when an agent’s action requires an
        ask. Each row matches a (resource, action) gate; the union
        of matching rows is the approver set. <code>*</code> is the
        wildcard sentinel; the founder catch-all is{' '}
        <code>(*, *, *, human, &lt;founder&gt;)</code>. The{' '}
        <code>*</code> action wildcard does not cover{' '}
        <code>iam.*</code> meta-actions — those need an explicit{' '}
        <code>(*, *, iam.*, …)</code> row.
      </p>

      {error && (
        <div
          style={{
            background: '#fff0f0',
            border: '1px solid #f5a5a5',
            color: '#900',
            padding: 8,
            borderRadius: 4,
            marginBottom: 8,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <form
        onSubmit={submitForm}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 6,
          marginBottom: 16,
          padding: 8,
          background: '#f7f7f9',
          borderRadius: 4,
        }}
      >
        <Field label="resource_kind">
          <input
            value={form.resource_kind}
            onChange={(e) => setForm((f) => ({ ...f, resource_kind: e.target.value }))}
            placeholder="* | tool | external_resource | secret | host | ..."
          />
        </Field>
        <Field label="resource_id">
          <input
            value={form.resource_id}
            onChange={(e) => setForm((f) => ({ ...f, resource_id: e.target.value }))}
            placeholder="* | <specific id>"
          />
        </Field>
        <Field label="action_pattern">
          <input
            value={form.action_pattern}
            onChange={(e) => setForm((f) => ({ ...f, action_pattern: e.target.value }))}
            placeholder="* | iam.* | tool.Bash | external_resource.ha.* | ..."
          />
        </Field>
        <Field label="approver_kind">
          <select
            value={form.approver_kind}
            onChange={(e) =>
              setForm((f) => ({ ...f, approver_kind: e.target.value as ApproverKind }))
            }
          >
            <option value="human">human</option>
            <option value="agent">agent</option>
            <option value="position">position</option>
            <option value="org_member">org_member (household member)</option>
          </select>
        </Field>
        <Field label="approver_id">
          {form.approver_kind === 'position' ? (
            <select
              value={form.approver_id}
              onChange={(e) => setForm((f) => ({ ...f, approver_id: e.target.value }))}
            >
              <option value="">— pick a position —</option>
              {positionsInOrg.map((p) => {
                const occupants = occupantsByPosition.get(p.id) ?? []
                const count = occupants.length
                return (
                  <option key={p.id} value={p.id}>
                    {p.name} ({count} agent{count === 1 ? '' : 's'})
                  </option>
                )
              })}
            </select>
          ) : (
            <input
              value={form.approver_id}
              onChange={(e) => setForm((f) => ({ ...f, approver_id: e.target.value }))}
              placeholder={
                form.approver_kind === 'org_member'
                  ? 'organization_id (auto-filled)'
                  : `id of ${form.approver_kind}`
              }
              disabled={form.approver_kind === 'org_member'}
            />
          )}
        </Field>
        {form.approver_kind === 'position' && form.approver_id && (
          <div
            style={{
              gridColumn: '1 / span 2',
              fontSize: 12,
              padding: '6px 8px',
              background: '#eef',
              borderRadius: 4,
              color: '#334',
            }}
          >
            <strong>Current occupants</strong> (delegated approver set at evaluation time):{' '}
            {(() => {
              const occupants = occupantsByPosition.get(form.approver_id) ?? []
              if (occupants.length === 0) {
                return (
                  <em style={{ color: '#900' }}>
                    none — requests routed to this position will hang until an agent is created
                  </em>
                )
              }
              return occupants.map((o) => o.name).join(', ')
            })()}
          </div>
        )}
        <div style={{ gridColumn: '1 / span 2', display: 'flex', gap: 6 }}>
          <button
            type="submit"
            disabled={busy}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: 4,
              border: '1px solid #0a84ff',
              background: '#0a84ff',
              color: 'white',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? '…' : editing ? `Save changes to #${editing.id}` : 'Add approver'}
          </button>
          {editing && (
            <button
              type="button"
              onClick={cancelEdit}
              disabled={busy}
              style={{
                padding: '8px 12px',
                borderRadius: 4,
                border: '1px solid #ddd',
                background: 'white',
                color: '#333',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {lonelyAgentGates.length > 0 && (
        <div
          style={{
            background: '#fff8e1',
            border: '1px solid #f0c040',
            color: '#5a4400',
            padding: 10,
            borderRadius: 4,
            marginBottom: 12,
            fontSize: 13,
          }}
          role="alert"
        >
          <strong>Agent-only approver{lonelyAgentGates.length === 1 ? '' : 's'} —
          add a human fallback.</strong>
          <p style={{ margin: '4px 0 6px 0' }}>
            {lonelyAgentGates.length === 1 ? 'This gate has' : 'These gates have'}{' '}
            only agents in the approver set. If the agent's daemon is
            offline, requests will hang with no human to fall back to.
            Add a human or household-member approver alongside each.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontFamily: 'monospace', fontSize: 12 }}>
            {lonelyAgentGates.map((group) => {
              const r = group[0]
              return (
                <li key={`${r.resource_kind}|${r.resource_id}|${r.action_pattern}`}>
                  {r.resource_kind}:{r.resource_id} / {r.action_pattern}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {catchAll.length > 0 && (
        <section style={{ marginBottom: 16 }}>
          <strong>Catch-all (covers everything not specified elsewhere)</strong>
          <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0 0' }}>
            {catchAll.map((r) => (
              <PolicyRow
                key={r.id}
                row={r}
                onDelete={() => onDelete(r)}
                onEdit={() => startEdit(r)}
                editing={editing?.id === r.id}
              />
            ))}
          </ul>
        </section>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <strong>
          {specific.length} specific {specific.length === 1 ? 'policy' : 'policies'}
        </strong>
        <button onClick={() => void refresh()} disabled={busy}>
          Refresh
        </button>
      </div>

      {specific.length === 0 ? (
        <p style={{ opacity: 0.6 }}>
          (no more-specific policies in this household; every ask falls through to the catch-all)
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {specific.map((r) => (
            <PolicyRow
              key={r.id}
              row={r}
              onDelete={() => onDelete(r)}
              onEdit={() => startEdit(r)}
              editing={editing?.id === r.id}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function describeRow(r: ApprovalPolicyRow): string {
  const gate = `(${r.resource_kind}, ${r.resource_id}, ${r.action_pattern})`
  const approver = r.approver_label
    ? `${r.approver_kind}: ${r.approver_label}`
    : `${r.approver_kind} ${r.approver_id}`
  return `gate ${gate} → ${approver}`
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ opacity: 0.7, fontFamily: 'monospace', fontSize: 11 }}>{label}</span>
      {children}
    </label>
  )
}

function PolicyRow({
  row,
  onDelete,
  onEdit,
  editing,
}: {
  row: ApprovalPolicyRow
  onDelete: () => void
  onEdit: () => void
  editing: boolean
}) {
  const approverDisplay = row.approver_label
    ? `${row.approver_label}`
    : `(unknown ${row.approver_kind} ${row.approver_id})`
  return (
    <li
      style={{
        padding: '10px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        background: editing ? '#f0f7ff' : undefined,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
          <span style={{ opacity: 0.5 }}>#{row.id}</span>{' '}
          <span>
            {row.resource_kind}:{row.resource_id}
          </span>{' '}
          / <span>{row.action_pattern}</span> →{' '}
          <strong>{row.approver_kind}</strong>{' '}
          <span>{approverDisplay}</span>
        </div>
        <div style={{ opacity: 0.5, fontSize: 11, marginTop: 2 }}>
          created {row.created_at}
          {editing && ' · editing'}
        </div>
      </div>
      <button
        onClick={onEdit}
        style={{
          padding: '4px 8px',
          borderRadius: 4,
          border: '1px solid #ddd',
          background: 'white',
          color: '#0a84ff',
          cursor: 'pointer',
          fontSize: 12,
        }}
      >
        Edit
      </button>
      <button
        onClick={onDelete}
        style={{
          padding: '4px 8px',
          borderRadius: 4,
          border: '1px solid #ddd',
          background: 'white',
          color: '#900',
          cursor: 'pointer',
          fontSize: 12,
        }}
      >
        Delete
      </button>
    </li>
  )
}
